import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";

import { AppShell } from "../components/layout/AppShell";

import { ProtectedRoute } from "./ProtectedRoute";
import { PageLoadingFallback } from "../components/common/PageLoadingFallback";

import { AllModules, AllActions } from "../accessManagement/AccessValues";

// Kept eager (not React.lazy): AppShell/ProtectedRoute (structural, needed
// on every route), ErrorFallback (the errorElement — must render without
// depending on a chunk fetch that could itself be what's failing), and
// Unauthorized (tiny, and a redirect target that should show instantly
// rather than making someone who was just denied wait on a fetch).
// Every actual page below is React.lazy(...) — each route's chunk now only
// downloads when that route is visited, instead of all of them bundled
// into (and downloaded for) every page load. AnimatedOutlet wraps the
// AppShell-nested routes in one shared <Suspense>; the few top-level
// routes below (login, reset/forgot password, "app") get their own since
// they render outside AppShell.
import Unauthorized from "./Unauthorized";
import ErrorFallback from "./ErrorFallback";
import { NotFoundPage } from "./NotFoundPage";
import {
  BulkGate,
  CalendarGate,
  ClauseGate,
  MonitorGate,
  ReceivableGate,
  SubscriptionGate,
} from "./ModuleFlagGate";
import { ModuleAwareNotFound } from "./ModuleAwareNotFound";
import { LicensingModuleKeys } from "../types/licensing.types";
import { isEmeisEnabled } from "../utils/emeisFlag";
import { isEmeisSignatureEnabled } from "../utils/emeisModuleFlags";
import { isEmeisAIEnabled } from "../utils/emeisAIFlag";

/**
 * Rewrites a legacy Library URL onto its /library equivalent, keeping the
 * rest of the path (ids, /edit, query string) intact.
 */
function LibraryPathRedirect({ from }: { from: string }) {
  const location = useLocation();
  const rest = location.pathname.replace(new RegExp(`^/${from}`), "");

  return (
    <Navigate
      to={`/library${rest}${location.search}${location.hash}`}
      replace
    />
  );
}

// Receivable templates open the document editor; lazy like every other page.
const EmailTemplatesPage = lazy(
  () =>
    import("../pages/receivables/receivablesTemplates/emailTemplate/EmailTemplate"),
);
const NoticeTemplatesPage = lazy(
  () =>
    import("../pages/receivables/receivablesTemplates/noticeTemplate/NoticeTemplate"),
);
const EmailTemplateEditor = lazy(
  () =>
    import("../pages/receivables/receivablesTemplates/emailTemplate/EmailTemplateEditor"),
);
const EmailTemplateDetail = lazy(
  () =>
    import("../pages/receivables/receivablesTemplates/emailTemplate/EmailTemplateDetails"),
);
const NoticeTemplateEditor = lazy(
  () =>
    import("../pages/receivables/receivablesTemplates/noticeTemplate/NoticeTemplateEditor"),
);
const NoticeTemplateDetails = lazy(
  () =>
    import("../pages/receivables/receivablesTemplates/noticeTemplate/NoticeTemplateDetails"),
);

const loadHome = () => import("../pages/dashboard/Dashboard");
const loadAnalyticsDashboard = () =>
  import("../pages/dashboard/AnalyticsDashboard");
const loadContractsList = () =>
  import("../pages/agreement/InProgress/ContractsListPage");

/**
 * Starts downloading the page being opened at the same time as the app
 * shell checks who is signed in. Left to React.lazy, a page's code was only
 * asked for once /Auth/me had answered and the route rendered — one more
 * round trip, in series, before the page could start loading its data.
 * The browser keeps the module, so the lazy() below resolves from it.
 */
const PRELOAD_ON_OPEN: Record<string, () => Promise<unknown>> = {
  "/": loadHome,
  "/dashboard": loadAnalyticsDashboard,
  "/contracts": loadContractsList,
};

export function preloadOpeningRoute(pathname: string) {
  const path = pathname.replace(/\/+$/, "") || "/";
  void PRELOAD_ON_OPEN[path]?.().catch(() => undefined);
}

const Dashboard = lazy(loadHome);
const HelpPage = lazy(() => import("../pages/help/HelpPage"));
const AnalyticsDashboard = lazy(loadAnalyticsDashboard);
const Party = lazy(() => import("../pages/party/Party"));
const Login = lazy(() => import("../pages/auth/Login"));
const RoleManagement = lazy(() => import("../pages/admin/RoleManagement"));
const OrganisationSettings = lazy(
  () => import("../pages/admin/organizationSettings/OrganisationSettings"),
);
const PartyDetail = lazy(() => import("../pages/party/PartyDetail"));
const RolePage = lazy(() => import("../pages/modals/role/RolePage"));
const Staff = lazy(() => import("../pages/staff/Staff"));
const StaffDetail = lazy(() => import("../pages/staff/StaffDetail"));
const ChangePassword = lazy(
  () => import("../pages/modals/Login/ChangePassword"),
);
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const OrganisationDetail = lazy(
  () => import("../pages/admin/organizationSettings/OrganisationDetail"),
);
const ProviderRegistryManagement = lazy(
  () => import("../pages/admin/providerRegistry/ProviderRegistryManagement"),
);
const AiAgentsPage = lazy(() => import("../pages/admin/aiAgents/AiAgentsPage"));
const AiModelsManagement = lazy(
  () => import("../pages/admin/aiModels/AiModelsManagement"),
);
const ApplicationStatusPage = lazy(
  () => import("../pages/appStatus/ApplicationStatusPage"),
);
const ReindexDataManagement = lazy(
  () => import("../pages/admin/reindex/ReindexDataManagement"),
);
const LawSignaDashboard = lazy(
  () => import("../pages/frontpage/LawSignaDashboard"),
);
const BusinessUnit = lazy(
  () => import("../pages/admin/businessUnit/BusinessUnit"),
);
const BusinessUnitDetail = lazy(
  () => import("../pages/admin/businessUnit/BusinessUnitDetail"),
);
const DepartmentSettings = lazy(
  () => import("../pages/admin/department/DepartmentSettings"),
);
const DepartmentDetail = lazy(
  () => import("../pages/admin/department/DepartmentDetail"),
);
const AgreementPage = lazy(
  () => import("../pages/agreement/newContract/AgreementPage"),
);
const ContractsListPage = lazy(loadContractsList);
const ContractDetail = lazy(
  () => import("../pages/agreement/InProgress/ContractDetail"),
);
const SigningCeremonyPage = lazy(
  () => import("../pages/signing/SigningCeremonyPage"),
);
const SigningSettingsPage = lazy(
  () => import("../pages/admin/signing/SigningSettingsPage"),
);
const BulkContract = lazy(
  () => import("../pages/agreement/bulkContract/BulkContract"),
);
const LegacyContract = lazy(
  () => import("../pages/agreement/legacyContract/LegacyContract"),
);
const LegacyContractList = lazy(
  () => import("../pages/agreement/legacyContractList/LegacyContractList"),
);
const LegacyContractDetail = lazy(
  () => import("../pages/agreement/legacyContractDetail/LegacyContractDetail"),
);
const BulkLegacyContract = lazy(
  () => import("../pages/agreement/bulkLegacyContract/BulkLegacyContract"),
);
const ObligationsHub = lazy(
  () => import("../pages/obligations/inProgress/ObligationsHub"),
);
const ObligationsMyWork = lazy(
  () => import("../pages/obligations/workflow/MyWorkPage"),
);
const MyCalendarPage = lazy(() => import("../pages/calendar/MyCalendarPage"));
const NotificationPreferencesPage = lazy(
  () => import("../pages/notifications/NotificationPreferencesPage"),
);
const ObligationsDashboard = lazy(
  () => import("../pages/obligations/workflow/ObligationDashboardPage"),
);
const ContractTemplateEditor = lazy(
  () => import("../pages/contractTemplates/ContractTemplateEditor"),
);
const AllContractTemplates = lazy(
  () => import("../pages/contractTemplates/AllContractTemplates"),
);
const ContractTemplateDetail = lazy(
  () => import("../pages/contractTemplates/ContractTemplateDetail"),
);
const AllClauseTemplates = lazy(
  () => import("../pages/clauseLibrary/AllClauseTemplates"),
);
const ClauseEditor = lazy(() => import("../pages/clauseLibrary/ClauseEditor"));
const ClauseDetailPage = lazy(
  () => import("../pages/clauseLibrary/ClauseDetailPage"),
);
const ContractTitle = lazy(
  () => import("../pages/admin/contractTitle/ContractTitle"),
);
const ContractCategory = lazy(
  () => import("../pages/admin/contractCategories/ContractCategories"),
);
const ContractType = lazy(
  () => import("../pages/admin/contractType/ContractType"),
);
const ClauseType = lazy(() => import("../pages/admin/clauseType/ClauseType"));
const Jurisdiction = lazy(
  () => import("../pages/admin/jurisdiction/Jurisdiction"),
);
const EntityType = lazy(() => import("../pages/admin/entityType/EntityType"));
const NotificationsPage = lazy(
  () => import("../pages/notifications/NotificationsPage"),
);
const TriggerReminders = lazy(() => import("../pages/admin/TriggerReminders"));
const LicenseManagement = lazy(
  () => import("../pages/admin/licensing/LicenseManagement"),
);
const OrganisationLicencePage = lazy(
  () => import("../pages/admin/licensing/OrganisationLicencePage"),
);
const SubscriptionUsage = lazy(
  () => import("../pages/admin/subscription/SubscriptionUsage"),
);
const AddNewObligation = lazy(
  () => import("../pages/obligations/newObligation/AddNewObligation"),
);
const ObligationsDetailsPage = lazy(
  () => import("../pages/obligations/inProgress/ObligationsDetailsPage"),
);
// Receivables Engine
const ReceivablesDashboard = lazy(
  () =>
    import("../pages/receivables/engine/dashboard/ReceivablesDashboardPage"),
);
const ReceivableEntries = lazy(
  () => import("../pages/receivables/engine/list/ReceivableEntriesPage"),
);
const ReceivableEntryPage = lazy(
  () => import("../pages/receivables/engine/detail/ReceivableEntryPage"),
);
const ReceivableEntryWizard = lazy(
  () => import("../pages/receivables/engine/create/ReceivableEntryWizard"),
);
const ReceivablesSettings = lazy(
  () => import("../pages/receivables/engine/settings/ReceivablesSettingsPage"),
);

export const router = createBrowserRouter([
  {
    path: "/login",

    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <Login />
      </Suspense>
    ),

    errorElement: <ErrorFallback />,
  },
  {
    path: "app",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<PageLoadingFallback />}>
          <LawSignaDashboard />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    path: "reset-password",

    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <ResetPassword />
      </Suspense>
    ),
    errorElement: <ErrorFallback />,
  },
  {
    path: "forgot-password",

    element: (
      <Suspense fallback={<PageLoadingFallback />}>
        <ForgotPassword />
      </Suspense>
    ),
    errorElement: <ErrorFallback />,
  },
  {
    path: "/unauthorized",

    element: <Unauthorized />,

    errorElement: <ErrorFallback />,
  },

  {
    path: "/",

    element: <AppShell />,

    errorElement: <ErrorFallback />,

    children: [
      /**
       * DASHBOARD
       */
      {
        index: true,

        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },

      /**
       * ANALYTICS DASHBOARD (unified contracts/obligations/receivables view)
       */
      {
        path: "dashboard",

        element: (
          <ProtectedRoute
            module={AllModules.Dashboard}
            action={AllActions.SEARCH}
          >
            <AnalyticsDashboard />
          </ProtectedRoute>
        ),
      },

      /**
       * Parties
       */
      {
        path: "party",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <Party />
          </ProtectedRoute>
        ),
      },

      {
        path: "party/:id",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <PartyDetail />
          </ProtectedRoute>
        ),
      },

      /**
       * Notifications
       */
      {
        path: "notifications",

        element: (
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        ),
      },

      /**
       * Notification preferences — the signed-in member's own channels per
       * category. Behind the "isEmeisCalendar" switch (utils/emeisModuleFlags).
       */
      {
        path: "notifications/preferences",
        element: (
          <CalendarGate>
            <ProtectedRoute>
              <NotificationPreferencesPage />
            </ProtectedRoute>
          </CalendarGate>
        ),
      },

      /**
       * My Calendar — contracts, obligations and receivables in one calendar.
       * Behind its own "isEmeisCalendar" switch (utils/emeisModuleFlags): with it
       * off the route behaves as if it did not exist, like its menu entry.
       */
      {
        path: "my-calendar",
        element: (
          <CalendarGate>
            <ProtectedRoute>
              <MyCalendarPage />
            </ProtectedRoute>
          </CalendarGate>
        ),
      },

      /**
       * Help — internal for now: with the "isEmeis" switch off the route
       * behaves as if it did not exist, matching its hidden menu entry.
       */
      {
        path: "help",

        element: (
          <ProtectedRoute>
            {isEmeisEnabled() ? <HelpPage /> : <NotFoundPage />}
          </ProtectedRoute>
        ),
      },

      /**
       * ADMIN SECTION
       *
       * The Admin menu is shown only to Org Admins and Super Admins
       * (nav.config adminOnly), so its list and management pages carry
       * `adminOnly` too; a module permission alone (e.g. Member:Search,
       * which ordinary users hold for member pickers) used to open them by
       * URL. Detail pages stay on their module permission: ordinary users
       * reach them from outside the Admin section, e.g. profiles
       * from the top bar, member cards, notifications, dashboard links.
       */

      /**
       * STAFF
       */
      {
        path: "admin/staffs",

        // Not adminOnly: a Dept Admin or BU Head reaches it too, and the
        // member search scopes the list to their department or unit.
        element: (
          <ProtectedRoute
            module={AllModules.Member}
            action={AllActions.SEARCH}
          >
            <Staff />
          </ProtectedRoute>
        ),
      },

      {
        path: "admin/staff/:id",

        // Any signed-in user may view a member's profile, their own or a
        // colleague's (top bar > Edit Profile, member cards, notifications).
        // Changes stay restricted: the page offers edits only to admins and
        // the profile's owner, and the backend enforces the same rule.
        element: (
          <ProtectedRoute>
            <StaffDetail />
          </ProtectedRoute>
        ),
      },

      /**
       * ADMIN ROLES
       */
      {
        path: "admin/roles",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Role}
            action={AllActions.SEARCH}
          >
            <RoleManagement />
          </ProtectedRoute>
        ),
      },

      {
        path: "admin/roles/manage",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Role}
            action={AllActions.SEARCH}
          >
            <RolePage />
          </ProtectedRoute>
        ),
      },

      {
        path: "admin/roles/manage/:id",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Role}
            action={AllActions.SEARCH}
          >
            <RolePage />
          </ProtectedRoute>
        ),
      },

      /**
       * ORGANIZATION
       */
      {
        path: "admin/organization",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Group}
            action={AllActions.SEARCH}
          >
            <OrganisationSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/organization/:id",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Group}
            action={AllActions.SEARCH}
          >
            <OrganisationDetail />
          </ProtectedRoute>
        ),
      },
      // Administration → Settings → E-Signature (isEmeisSignature). Org and
      // super admins; the API refuses writes from anyone else.
      {
        path: "admin/signing",
        element: (
          <ProtectedRoute adminOnly>
            {isEmeisSignatureEnabled() ? <SigningSettingsPage /> : <NotFoundPage />}
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/providers",

        element: (
          <ProtectedRoute
            module={AllModules.Group}
            action={AllActions.SEARCH}
            superAdminOnly
          >
            <ProviderRegistryManagement />
          </ProtectedRoute>
        ),
      },
      // Super admins only, and only while the enableEmeisAI switch is on; the
      // AI service checks super-admin rights again on every call.
      {
        path: "admin/ai",
        element: (
          <ProtectedRoute superAdminOnly>
            {isEmeisAIEnabled() ? (
              <Navigate to="/admin/ai-models" replace />
            ) : (
              <NotFoundPage />
            )}
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/ai-agents",
        element: (
          <ProtectedRoute superAdminOnly>
            {isEmeisAIEnabled() ? <AiAgentsPage /> : <NotFoundPage />}
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/ai-models",

        element: (
          <ProtectedRoute superAdminOnly>
            {isEmeisAIEnabled() ? <AiModelsManagement /> : <NotFoundPage />}
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/application-status",
        element: (
          <ProtectedRoute superAdminOnly>
            <MonitorGate>
              <ApplicationStatusPage />
            </MonitorGate>
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/reindex",

        element: (
          <ProtectedRoute superAdminOnly>
            <MonitorGate>
              <ReindexDataManagement />
            </MonitorGate>
          </ProtectedRoute>
        ),
      },

      // Every scheduled reminder, run on demand from one page.
      {
        path: "admin/trigger-reminders",

        element: (
          <ProtectedRoute adminOnly>
            <TriggerReminders />
          </ProtectedRoute>
        ),
      },
      // The former one-per-job pages, kept as links that still work.
      {
        path: "admin/renewal-reminders",
        element: <Navigate to="/admin/trigger-reminders" replace />,
      },
      {
        path: "admin/obligation-reminders",
        element: <Navigate to="/admin/trigger-reminders" replace />,
      },

      /**
       * LICENSING — the organisation's subscription, usage and recharges
       * (Org Admin) and the Super Admin plan/entitlement console. Behind the
       * "isEmeisSubscription" switch (utils/emeisModuleFlags).
       */
      {
        path: "admin/subscription",

        element: (
          <SubscriptionGate>
            <ProtectedRoute adminOnly>
              <SubscriptionUsage />
            </ProtectedRoute>
          </SubscriptionGate>
        ),
      },
      {
        path: "admin/licensing",

        element: (
          <SubscriptionGate>
            <ProtectedRoute
              module={AllModules.Group}
              action={AllActions.SEARCH}
              superAdminOnly
            >
              <LicenseManagement />
            </ProtectedRoute>
          </SubscriptionGate>
        ),
      },
      {
        path: "admin/licensing/organisations/:id",

        element: (
          <SubscriptionGate>
            <ProtectedRoute
              module={AllModules.Group}
              action={AllActions.SEARCH}
              superAdminOnly
            >
              <OrganisationLicencePage />
            </ProtectedRoute>
          </SubscriptionGate>
        ),
      },

      // Business Units
      {
        path: "admin/businessUnit",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.BusinessUnit}
            action={AllActions.SEARCH}
          >
            <BusinessUnit />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/businessUnits/:id",

        element: (
          <ProtectedRoute
            module={AllModules.BusinessUnit}
            action={AllActions.SEARCH}
          >
            <BusinessUnitDetail />
          </ProtectedRoute>
        ),
      },

      // Department
      {
        path: "admin/department",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Department}
            action={AllActions.SEARCH}
          >
            <DepartmentSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/department/:id",

        element: (
          <ProtectedRoute
            module={AllModules.Department}
            action={AllActions.SEARCH}
          >
            <DepartmentDetail />
          </ProtectedRoute>
        ),
      },

      {
        path: "admin/contract-title",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <ContractTitle />
          </ProtectedRoute>
        ),
      },

      {
        path: "admin/contract-category",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <ContractCategory />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/contract-type",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <ContractType />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/clause-type",

        element: (
          <ClauseGate>
            <ProtectedRoute
              adminOnly
              module={AllModules.Contract}
              action={AllActions.SEARCH}
            >
              <ClauseType />
            </ProtectedRoute>
          </ClauseGate>
        ),
      },
      {
        path: "admin/jurisdiction",

        element: (
          <ClauseGate>
            <ProtectedRoute
              adminOnly
              module={AllModules.Contract}
              action={AllActions.SEARCH}
            >
              <Jurisdiction />
            </ProtectedRoute>
          </ClauseGate>
        ),
      },
      {
        path: "admin/entity-type",

        element: (
          <ProtectedRoute
            adminOnly
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <EntityType />
          </ProtectedRoute>
        ),
      },

      // Contracts
      {
        path: "contracts",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <ContractsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "contracts/new",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.CREATE}
          >
            <AgreementPage />
          </ProtectedRoute>
        ),
      },
      // Pactline Native Signature: the signing ceremony, only while the
      // isEmeisSignature switch is on (the API checks the signer's part).
      {
        path: "contracts/sign/:requestId",
        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            {isEmeisSignatureEnabled() ? <SigningCeremonyPage /> : <NotFoundPage />}
          </ProtectedRoute>
        ),
      },
      {
        path: "contracts/:id",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <ContractDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "contracts/:id/edit",

        element: (
          <ProtectedRoute module={AllModules.Contract} action={AllActions.EDIT}>
            <AgreementPage />
          </ProtectedRoute>
        ),
      },

      {
        path: "contracts/bulk",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.CREATE}
          >
            <BulkGate>
              <BulkContract />
            </BulkGate>
          </ProtectedRoute>
        ),
      },

      {
        path: "contracts/legacy-list",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <LegacyContractList />
          </ProtectedRoute>
        ),
      },

      {
        // New and edit share one form; the presence of :id decides which.
        path: "contracts/legacy/new",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.CREATE}
          >
            <LegacyContract />
          </ProtectedRoute>
        ),
      },

      {
        path: "contracts/legacy/:id/edit",

        element: (
          <ProtectedRoute module={AllModules.Contract} action={AllActions.EDIT}>
            <LegacyContract />
          </ProtectedRoute>
        ),
      },

      {
        path: "contracts/legacy/:id",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <LegacyContractDetail />
          </ProtectedRoute>
        ),
      },

      {
        path: "contracts/legacy",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.SEARCH}
          >
            <LegacyContract />
          </ProtectedRoute>
        ),
      },

      {
        path: "contracts/bulk-legacy",

        element: (
          <ProtectedRoute
            module={AllModules.Contract}
            action={AllActions.CREATE}
          >
            <BulkGate>
              <BulkLegacyContract />
            </BulkGate>
          </ProtectedRoute>
        ),
      },

      // library — contract templates
      {
        path: "library/contract-templates/new",

        element: (
          <ProtectedRoute
            planModule={LicensingModuleKeys.ContractStudio}
            module={AllModules.Template}
            action={AllActions.CREATE}
          >
            <ContractTemplateEditor />
          </ProtectedRoute>
        ),
      },
      {
        path: "library/contract-templates/all",

        element: (
          <ProtectedRoute
            planModule={LicensingModuleKeys.ContractStudio}
            module={AllModules.Template}
            action={AllActions.SEARCH}
          >
            <AllContractTemplates />
          </ProtectedRoute>
        ),
      },
      {
        path: "library/contract-templates/:id",

        element: (
          <ProtectedRoute
            planModule={LicensingModuleKeys.ContractStudio}
            module={AllModules.Template}
            action={AllActions.SEARCH}
          >
            <ContractTemplateDetail />
          </ProtectedRoute>
        ),
      },
      {
        path: "library/contract-templates/:id/edit",

        element: (
          <ProtectedRoute
            planModule={LicensingModuleKeys.ContractStudio}
            module={AllModules.Template}
            action={AllActions.EDIT}
          >
            <ContractTemplateEditor />
          </ProtectedRoute>
        ),
      },

      // library — clause templates (Clause Library; only while the "isEmeisClause" switch is on)
      {
        path: "library/clause-templates/new",

        element: (
          <ClauseGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ContractStudio}
              module={AllModules.Template}
              action={AllActions.CREATE}
            >
              <ClauseEditor />
            </ProtectedRoute>
          </ClauseGate>
        ),
      },
      {
        path: "library/clause-templates/all",

        element: (
          <ClauseGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ContractStudio}
              module={AllModules.Template}
              action={AllActions.SEARCH}
            >
              <AllClauseTemplates />
            </ProtectedRoute>
          </ClauseGate>
        ),
      },
      {
        path: "library/clause-templates/:id",

        element: (
          <ClauseGate>
            {/* No module permission here: the approvers an author names may hold none, and
                still open the clause from their notification. The API decides who may read it. */}
            <ProtectedRoute planModule={LicensingModuleKeys.ContractStudio}>
              <ClauseDetailPage />
            </ProtectedRoute>
          </ClauseGate>
        ),
      },
      {
        path: "library/clause-templates/:id/edit",

        element: (
          <ClauseGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ContractStudio}
              module={AllModules.Template}
              action={AllActions.EDIT}
            >
              <ClauseEditor />
            </ProtectedRoute>
          </ClauseGate>
        ),
      },

      // Library moved out from under Contracts Studio, so its URLs moved with
      // it: /contracts/library/* and /contracts-studio/library/* -> /library/*.
      // Kept as redirects because those paths have been linked and bookmarked,
      // and a bare rename would land them on the 404 page. replace so the old
      // URL doesn't sit in history behind the new one.
      {
        path: "contracts/library/*",
        element: <LibraryPathRedirect from="contracts/library" />,
      },
      {
        path: "contracts-studio/library/*",
        element: <LibraryPathRedirect from="contracts-studio/library" />,
      },
      {
        path: "contracts-studio/library",
        element: <Navigate to="/library/contract-templates/all" replace />,
      },

      // obligations hub
      // The hub opens on My Work; the redirect keeps the sidebar item selected.
      {
        path: "obligations-hub",
        element: <Navigate to="/obligations-hub/my-work" replace />,
      },
      {
        // The obligation calendar is now part of My Calendar; old links land there.
        path: "obligations-hub/calendar",
        element: <Navigate to="/my-calendar" replace />,
      },
      {
        path: "obligations-hub/dashboard",

        element: (
          <ProtectedRoute
            module={AllModules.Obligation}
            action={AllActions.SEARCH}
          >
            <ObligationsDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "obligations-hub/my-work",

        element: (
          <ProtectedRoute
            module={AllModules.Obligation}
            action={AllActions.SEARCH}
          >
            <ObligationsMyWork />
          </ProtectedRoute>
        ),
      },
      {
        path: "obligations-hub/new",

        element: (
          <ProtectedRoute
            module={AllModules.Obligation}
            action={AllActions.CREATE}
          >
            <AddNewObligation />
          </ProtectedRoute>
        ),
      },
      {
        path: "obligations-hub/:id",

        element: (
          <ProtectedRoute
            module={AllModules.Obligation}
            action={AllActions.SEARCH}
          >
            <ObligationsDetailsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "obligations-hub/:id/edit",
        element: (
          <ProtectedRoute
            module={AllModules.Obligation}
            action={AllActions.EDIT}
          >
            {/* Edit runs through the same wizard as create, prefilled. */}
            <AddNewObligation />
          </ProtectedRoute>
        ),
      },
      {
        path: "obligations-hub/all",

        element: (
          <ProtectedRoute
            module={AllModules.Obligation}
            action={AllActions.SEARCH}
          >
            <ObligationsHub />
          </ProtectedRoute>
        ),
      },
      // The contract list was removed; a saved link lands on the obligations list instead.
      {
        path: "obligations-hub/contracts",
        element: <Navigate to="/obligations-hub/all" replace />,
      },

      // receivables engine
      // Fixed paths (dashboard, all, new, settings, templates/...) are listed
      // before ":id"; React Router ranks static segments above dynamic ones
      // anyway, so an entry id can never shadow them.
      {
        path: "receivables-engine",
        element: (
          <ReceivableGate>
            <Navigate to="/receivables-engine/dashboard" replace />
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/dashboard",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              module={AllModules.Receivable}
              action={AllActions.SEARCH}
            >
              <ReceivablesDashboard />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/all",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              module={AllModules.Receivable}
              action={AllActions.SEARCH}
            >
              <ReceivableEntries />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/new",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              module={AllModules.Receivable}
              action={AllActions.CREATE}
            >
              <ReceivableEntryWizard />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        // The settings moved to Admin → Workflow → Receivables Settings; old links land there.
        path: "receivables-engine/settings",
        element: (
          <ReceivableGate>
            <Navigate to="/admin/workflow/receivables" replace />
          </ReceivableGate>
        ),
      },
      {
        // Admin → Workflow opens its first (today its only) section.
        path: "admin/workflow",
        element: (
          <ReceivableGate>
            <Navigate to="/admin/workflow/receivables" replace />
          </ReceivableGate>
        ),
      },
      {
        // Admin → Workflow → Receivables Settings, Super Admin only.
        path: "admin/workflow/receivables",
        element: (
          <ReceivableGate>
            <ProtectedRoute module={AllModules.Receivable} superAdminOnly>
              <ReceivablesSettings />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/email",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.SEARCH}
            >
              <EmailTemplatesPage />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/email/new",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.CREATE}
            >
              <EmailTemplateEditor />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/email/:id",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.SEARCH}
            >
              <EmailTemplateDetail />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/email/:id/edit",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.EDIT}
            >
              <EmailTemplateEditor />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/notice",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.SEARCH}
            >
              <NoticeTemplatesPage />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/notice/new",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.CREATE}
            >
              <NoticeTemplateEditor />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/notice/:id",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.SEARCH}
            >
              <NoticeTemplateDetails />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/templates/notice/:id/edit",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              planModule={LicensingModuleKeys.ReceivablesEngine}
              module={AllModules.ReceivableTemplate}
              action={AllActions.EDIT}
            >
              <NoticeTemplateEditor />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/:id",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              module={AllModules.Receivable}
              action={AllActions.SEARCH}
            >
              <ReceivableEntryPage />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },
      {
        path: "receivables-engine/:id/edit",
        element: (
          <ReceivableGate>
            <ProtectedRoute
              module={AllModules.Receivable}
              action={AllActions.EDIT}
            >
              <ReceivableEntryWizard />
            </ProtectedRoute>
          </ReceivableGate>
        ),
      },

      {
        path: "change-password",

        element: (
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        ),
      },

      // Catch-all, last so it only matches once every real route has been
      // tried. Inside the authenticated layout on purpose: a mistyped URL
      // keeps the sidebar and topbar, so the reader can navigate on instead
      // of landing on a bare page with nothing but a back button.
      // Previously nothing matched an unknown path and the app rendered blank.
      {
        path: "*",
        element: (
          <ProtectedRoute>
            <ModuleAwareNotFound />
          </ProtectedRoute>
        ),
      },
    ],
  },

  // Same, for anyone not signed in — without this an unknown public URL
  // (a stale link from an email, say) also rendered nothing.
  {
    path: "*",
    element: <NotFoundPage />,
    errorElement: <ErrorFallback />,
  },
]);
